import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Ruler, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CATEGORY_TO_CHART = {
  tshirts: "oversized_tshirt", sweatshirts: "sweatshirt", hoodies: "sweatshirt",
  shirts: "shirt", trousers: "trouser", "baggy-jeans": "baggy_jeans",
  tops: "regular_tshirt", dresses: "regular_tshirt", "co-ords": "oversized_tshirt",
  jackets: "sweatshirt", "jackets-women": "sweatshirt", skirts: "trouser",
};

export const chartKeyFor = (product) => {
  if (!product) return "oversized_tshirt";
  const base = CATEGORY_TO_CHART[product.category] || "oversized_tshirt";
  if (base === "oversized_tshirt" && /regular/i.test(product.fit || "")) return "regular_tshirt";
  return base;
};

const PROFILE_KEY = "vv_fit_profile";
const RECS_KEY = "vv_fit_recs";

export const getFitRecs = () => { try { return JSON.parse(localStorage.getItem(RECS_KEY) || "{}"); } catch { return {}; } };

const inputCls = "w-full bg-transparent border border-vv-black/20 focus:border-vv-black outline-none px-3 py-2.5 text-sm placeholder:text-vv-stone transition-colors";
const labelCls = "text-[10px] uppercase tracking-[0.2em] text-vv-muted";

const SHAPES = [["slim", "Slim"], ["athletic", "Athletic"], ["average", "Average"], ["broad", "Broad"]];
const FITS = [["slim", "Slim Fit"], ["regular", "Regular Fit"], ["relaxed", "Relaxed Fit"], ["oversized", "Oversized Fit"]];

const MEASURE_TIPS = {
  chest: "Wrap the tape around the fullest part of your chest, under your arms. Keep it level and snug, not tight.",
  shoulder: "Measure straight across the back from the tip of one shoulder to the other.",
  waist: "Measure around your natural waistline, just above the navel, keeping the tape comfortably loose.",
  hip: "Measure around the fullest part of your hips, feet together.",
  inseam: "Measure from the crotch seam down the inside of your leg to where you want the hem.",
};

// Minimal line-art figure with the measured zone highlighted
function MeasureFigure({ field }) {
  const stroke = "#A7A39B";
  const hi = "#1C1B1A";
  return (
    <svg viewBox="0 0 60 100" className="w-10 h-16 shrink-0" fill="none" aria-hidden>
      <circle cx="30" cy="10" r="7" stroke={stroke} strokeWidth="1.5" />
      <path d="M18 22 Q30 17 42 22 L44 58 Q30 63 16 58 Z" stroke={stroke} strokeWidth="1.5" />
      <path d="M17 60 L15 96 M43 60 L45 96" stroke={stroke} strokeWidth="1.5" />
      {field === "chest" && <ellipse cx="30" cy="34" rx="14.5" ry="4" stroke={hi} strokeWidth="2" strokeDasharray="3 2" />}
      {field === "shoulder" && <path d="M18 23 L42 23" stroke={hi} strokeWidth="2" strokeDasharray="3 2" />}
      {field === "waist" && <ellipse cx="30" cy="50" rx="13" ry="3.5" stroke={hi} strokeWidth="2" strokeDasharray="3 2" />}
      {field === "hip" && <ellipse cx="30" cy="62" rx="14" ry="4" stroke={hi} strokeWidth="2" strokeDasharray="3 2" />}
      {field === "inseam" && <path d="M31 64 L32.5 95" stroke={hi} strokeWidth="2" strokeDasharray="3 2" />}
    </svg>
  );
}

const STEPS = ["Product", "Body", "Measurements", "Fit", "Usual Size", "Recommendation"];

export default function SizeFinder({ product, open, onClose, onAddToCart }) {
  const reduce = useReducedMotion();
  const [charts, setCharts] = useState([]);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [needField, setNeedField] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [heightUnit, setHeightUnit] = useState("cm");
  const [a, setA] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(PROFILE_KEY) || "{}"); } catch {}
    return {
      chart_key: chartKeyFor(product), height_cm: "", height_ft: "", height_in: "", weight_kg: "",
      body_shape: "average", gender: "men", chest: "", shoulder: "", waist: "", hip: "", inseam: "",
      fit_preference: "regular", usual_size: "", usual_fits: "", ...saved,
      chart_key: chartKeyFor(product),
    };
  });

  const chart = useMemo(() => charts.find((c) => c.key === a.chart_key), [charts, a.chart_key]);
  const isBottom = chart?.kind === "bottom";
  const measureFields = isBottom ? ["waist", "hip", "inseam"] : ["chest", "shoulder"];
  const requiredField = isBottom ? "waist" : "chest";

  useEffect(() => {
    api.get("/size-charts").then((r) => setCharts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      setA((prev) => ({ ...prev, chart_key: chartKeyFor(product) }));
      const recs = getFitRecs();
      const key = chartKeyFor(product);
      if (recs[key]) { setResult(recs[key]); setStep(5); } else { setResult(null); setStep(0); }
      setFeedback(null);
      setNeedField(null);
      api.post("/fit/event", { type: "open", category: key }).catch(() => {});
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });

  const heightCm = () => {
    if (heightUnit === "cm") return parseFloat(a.height_cm) || null;
    const ft = parseFloat(a.height_ft) || 0, inch = parseFloat(a.height_in) || 0;
    return ft || inch ? Math.round((ft * 30.48 + inch * 2.54) * 10) / 10 : null;
  };

  const validate = () => {
    if (step === 1) {
      const h = heightCm();
      if (!h || h < 120 || h > 230) return "Enter a valid height (120–230 cm).";
      const w = parseFloat(a.weight_kg);
      if (!w || w < 35 || w > 220) return "Enter a valid weight in kg (35–220).";
    }
    if (step === 2) {
      const v = parseFloat(a[requiredField]);
      if (!v || v < 40 || v > 220) return `Your ${requiredField} measurement is needed for an accurate recommendation (in cm).`;
    }
    return null;
  };

  const next = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (step < 4) { setDir(1); setStep(step + 1); return; }
    // submit
    setLoading(true);
    setDir(1);
    try {
      const payload = {
        chart_key: a.chart_key, height_cm: heightCm(), weight_kg: parseFloat(a.weight_kg) || null,
        body_shape: a.body_shape, gender: a.gender,
        chest: parseFloat(a.chest) || null, shoulder: parseFloat(a.shoulder) || null,
        waist: parseFloat(a.waist) || null, hip: parseFloat(a.hip) || null, inseam: parseFloat(a.inseam) || null,
        fit_preference: a.fit_preference, usual_size: a.usual_size || null, usual_fits: a.usual_fits || null,
      };
      await new Promise((r) => setTimeout(r, 700));
      const { data } = await api.post("/fit/recommend", payload);
      if (data.status === "need_measurements") {
        setNeedField(data.missing[0]);
        setStep(2);
        setDir(-1);
        toast.error(data.message);
      } else {
        setResult(data);
        setNeedField(null);
        setStep(5);
        const recs = getFitRecs();
        recs[a.chart_key] = data;
        try { localStorage.setItem(RECS_KEY, JSON.stringify(recs)); } catch {}
        const { usual_size, usual_fits, ...profile } = a;
        try { localStorage.setItem(PROFILE_KEY, JSON.stringify(profile)); } catch {}
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const back = () => {
    setNeedField(null);
    setDir(-1);
    setStep(Math.max(0, step - 1));
  };

  const sendFeedback = (val) => {
    setFeedback(val);
    api.post("/fit/event", { type: "feedback", category: a.chart_key, size: result?.size, feedback: val }).catch(() => {});
  };

  const addToBag = () => {
    api.post("/fit/event", { type: "add_to_cart", category: a.chart_key, size: result?.size }).catch(() => {});
    onAddToCart(result?.size);
  };

  if (!open) return null;
  const transition = reduce ? { duration: 0 } : { duration: 0.35, ease: [0.22, 1, 0.36, 1] };
  const sizesForUsual = chart?.sizes?.map((s) => s.size) || [];

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-vv-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <motion.div
        data-testid="fit-finder-modal" role="dialog" aria-modal="true" aria-label="Find your perfect fit"
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-vv-ivory w-full sm:max-w-lg max-h-[94vh] sm:max-h-[88vh] overflow-y-auto flex flex-col"
      >
        <div className="sticky top-0 bg-vv-ivory z-10 px-6 sm:px-8 pt-6 pb-4 border-b border-vv-black/10">
          <div className="flex items-center justify-between mb-4">
            <p className="vv-overline flex items-center gap-2"><Ruler size={13} strokeWidth={1.5} /> Find Your Perfect Fit</p>
            <button data-testid="fit-close" onClick={onClose} aria-label="Close fit finder"><X size={20} strokeWidth={1.25} /></button>
          </div>
          <div className="h-px bg-vv-black/10 relative overflow-hidden">
            <motion.div data-testid="fit-progress" className="absolute inset-y-0 left-0 bg-vv-black"
              animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={transition} />
          </div>
          <p className="text-[9px] uppercase tracking-[0.25em] text-vv-stone mt-2">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        </div>

        <div className="px-6 sm:px-8 py-7 flex-1">
          <AnimatePresence mode="wait" initial={false} custom={dir}>
            <motion.div key={step} custom={dir}
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: dir * 28 }}
              animate={{ opacity: 1, x: 0 }} exit={reduce ? { opacity: 0 } : { opacity: 0, x: dir * -28 }}
              transition={transition}>

              {step === 0 && (
                <div data-testid="fit-step-product">
                  <h3 className="font-display text-2xl tracking-tight mb-6">What are we fitting?</h3>
                  <div className="space-y-2">
                    {charts.map((c) => (
                      <button key={c.key} data-testid={`fit-type-${c.key}`} onClick={() => setA({ ...a, chart_key: c.key })}
                        className={`w-full text-left px-4 py-3.5 border text-sm transition-colors flex items-center justify-between ${a.chart_key === c.key ? "border-vv-black bg-vv-black text-vv-ivory" : "border-vv-black/15 hover:border-vv-black"}`}>
                        {c.label}
                        {a.chart_key === c.key && <Check size={14} />}
                      </button>
                    ))}
                  </div>
                  {product && <p className="text-[11px] text-vv-muted mt-4">Detected from this product: {chart?.label}</p>}
                </div>
              )}

              {step === 1 && (
                <div data-testid="fit-step-body" className="space-y-5">
                  <h3 className="font-display text-2xl tracking-tight">Your body details</h3>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={labelCls}>Height</label>
                      <div className="flex gap-2 text-[10px] uppercase tracking-[0.15em]">
                        {[["cm", "cm"], ["ft", "ft / in"]].map(([v, l]) => (
                          <button key={v} data-testid={`fit-height-unit-${v}`} onClick={() => setHeightUnit(v)}
                            className={heightUnit === v ? "font-medium border-b border-vv-black" : "text-vv-stone"}>{l}</button>
                        ))}
                      </div>
                    </div>
                    {heightUnit === "cm" ? (
                      <input data-testid="fit-height" inputMode="decimal" value={a.height_cm} onChange={set("height_cm")} placeholder="e.g. 178 cm" className={inputCls} />
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="fit-height-ft" inputMode="numeric" value={a.height_ft} onChange={set("height_ft")} placeholder="ft — e.g. 5" className={inputCls} />
                        <input data-testid="fit-height-in" inputMode="numeric" value={a.height_in} onChange={set("height_in")} placeholder="in — e.g. 10" className={inputCls} />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Weight (kg)</label>
                    <input data-testid="fit-weight" inputMode="decimal" value={a.weight_kg} onChange={set("weight_kg")} placeholder="e.g. 72" className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <label className={labelCls}>Body shape</label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {SHAPES.map(([v, l]) => (
                        <button key={v} data-testid={`fit-shape-${v}`} onClick={() => setA({ ...a, body_shape: v })}
                          className={`px-2 py-2.5 text-[11px] uppercase tracking-wider border transition-colors ${a.body_shape === v ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/15 hover:border-vv-black"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Sizing category</label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[["men", "Men's"], ["women", "Women's"]].map(([v, l]) => (
                        <button key={v} data-testid={`fit-gender-${v}`} onClick={() => setA({ ...a, gender: v })}
                          className={`px-2 py-2.5 text-[11px] uppercase tracking-wider border transition-colors ${a.gender === v ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/15 hover:border-vv-black"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="fit-step-measure" className="space-y-6">
                  <h3 className="font-display text-2xl tracking-tight">Measurements</h3>
                  <p className="text-xs text-vv-muted -mt-3">In centimetres. {isBottom ? "Waist" : "Chest"} is required — the rest refine the fit.</p>
                  {measureFields.map((f, i) => (
                    <div key={f} className={`border p-4 transition-colors ${needField === f ? "border-vv-burgundy" : "border-vv-black/10"}`}>
                      <div className="flex gap-4 items-center">
                        <MeasureFigure field={f} />
                        <div className="flex-1">
                          <label className={labelCls}>{f.charAt(0).toUpperCase() + f.slice(1)} (cm){f === requiredField ? " — required" : " — optional"}</label>
                          <input data-testid={`fit-measure-${f}`} inputMode="decimal" value={a[f]} onChange={set(f)}
                            placeholder={f === "chest" ? "e.g. 98" : f === "waist" ? "e.g. 82" : "Optional"}
                            className={`${inputCls} mt-2`} />
                        </div>
                      </div>
                      <p className="text-[11px] text-vv-muted mt-3 leading-relaxed">{MEASURE_TIPS[f]}</p>
                    </div>
                  ))}
                </div>
              )}

              {step === 3 && (
                <div data-testid="fit-step-preference">
                  <h3 className="font-display text-2xl tracking-tight mb-6">How do you like it to fit?</h3>
                  <div className="space-y-2">
                    {FITS.map(([v, l]) => (
                      <button key={v} data-testid={`fit-pref-${v}`} onClick={() => setA({ ...a, fit_preference: v })}
                        className={`w-full text-left px-4 py-3.5 border text-sm transition-colors flex items-center justify-between ${a.fit_preference === v ? "border-vv-black bg-vv-black text-vv-ivory" : "border-vv-black/15 hover:border-vv-black"}`}>
                        {l}
                        {a.fit_preference === v && <Check size={14} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="fit-step-usual" className="space-y-6">
                  <h3 className="font-display text-2xl tracking-tight">What do you usually wear?</h3>
                  <div>
                    <label className={labelCls}>Usual size in {chart?.label}</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {sizesForUsual.map((s) => (
                        <button key={s} data-testid={`fit-usual-${s.toLowerCase()}`} onClick={() => setA({ ...a, usual_size: s })}
                          className={`min-w-11 px-3 py-2.5 text-[11px] tracking-widest border transition-colors ${a.usual_size === s ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20 hover:border-vv-black"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>How does it usually feel?</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[["tight", "Too tight"], ["good", "Just right"], ["loose", "Too loose"]].map(([v, l]) => (
                        <button key={v} data-testid={`fit-usualfeel-${v}`} onClick={() => setA({ ...a, usual_fits: v })}
                          className={`px-2 py-2.5 text-[11px] uppercase tracking-wider border transition-colors ${a.usual_fits === v ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/15 hover:border-vv-black"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && result && (
                <div data-testid="fit-step-result">
                  <p className="vv-overline text-vv-muted mb-3">Our recommendation</p>
                  <div className="flex items-end gap-5">
                    <p data-testid="fit-rec-size" className="font-display text-6xl tracking-tight leading-none">{result.size}</p>
                    <div className="pb-1.5">
                      {result.alt_size && <p data-testid="fit-alt-size" className="text-[11px] text-vv-muted">Also consider: <span className="text-vv-black font-medium">{result.alt_size}</span></p>}
                      <p className="text-[10px] uppercase tracking-[0.2em] text-vv-forest mt-1" data-testid="fit-confidence">{result.confidence} confidence · {result.fit_preference} fit</p>
                    </div>
                  </div>
                  <p data-testid="fit-explanation" className="text-sm text-vv-muted leading-relaxed mt-5">{result.explanation}</p>

                  {result.compare?.alternative && (
                    <div className="mt-6 border border-vv-black/10">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted px-4 pt-3">Compare sizes (garment, cm)</p>
                      <table className="w-full text-xs mt-2" data-testid="fit-compare">
                        <thead>
                          <tr className="text-left text-vv-stone border-b border-vv-black/10">
                            <th className="px-4 py-2 font-normal">Measure</th>
                            <th className="py-2 font-normal">{result.size} (you)</th>
                            <th className="py-2 pr-4 font-normal">{result.alt_size}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.compare.keys.map((k) => (
                            <tr key={k} className="border-b border-vv-black/5">
                              <td className="px-4 py-2 capitalize">{k}</td>
                              <td className="py-2">{result.compare.recommended[k]}</td>
                              <td className="py-2 pr-4">{result.compare.alternative[k]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button data-testid="fit-add-to-bag" onClick={addToBag} className="vv-btn-dark w-full mt-7">
                    Add to Bag — Size {result.size}
                  </button>

                  <div className="flex items-center justify-between mt-6 pt-5 border-t border-vv-black/10">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Was this helpful?</p>
                    {feedback ? (
                      <p className="text-[11px] text-vv-forest">Thank you for the feedback.</p>
                    ) : (
                      <div className="flex gap-2">
                        <button data-testid="fit-feedback-helpful" onClick={() => sendFeedback("helpful")} aria-label="Recommendation was helpful"
                          className="w-9 h-9 border border-vv-black/15 flex items-center justify-center hover:border-vv-black transition-colors"><ThumbsUp size={14} strokeWidth={1.5} /></button>
                        <button data-testid="fit-feedback-wrong" onClick={() => sendFeedback("wrong")} aria-label="Recommendation was wrong"
                          className="w-9 h-9 border border-vv-black/15 flex items-center justify-center hover:border-vv-black transition-colors"><ThumbsDown size={14} strokeWidth={1.5} /></button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {step < 5 && (
          <div className="sticky bottom-0 bg-vv-ivory border-t border-vv-black/10 px-6 sm:px-8 py-4 flex items-center justify-between gap-3">
            <button data-testid="fit-back" onClick={back} disabled={step === 0}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-vv-muted hover:text-vv-black disabled:opacity-30 transition-colors">
              <ArrowLeft size={13} /> Back
            </button>
            {step === 4 && (
              <button data-testid="fit-skip" onClick={() => { setA({ ...a, usual_size: "", usual_fits: "" }); next(); }}
                className="text-[11px] uppercase tracking-[0.2em] text-vv-stone hover:text-vv-black transition-colors">Skip</button>
            )}
            <button data-testid="fit-continue" onClick={next} disabled={loading} className="vv-btn-dark !px-6 !py-3 min-w-36 disabled:opacity-50">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-3.5 h-3.5 border border-vv-ivory/40 border-t-vv-ivory rounded-full animate-spin" /> Calculating…
                </span>
              ) : step === 4 ? (<>Get Recommendation <ArrowRight size={13} /></>) : (<>Continue <ArrowRight size={13} /></>)}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
